import React from 'react';
import { usePortal } from '../../contexts/PortalContext';
import type { Deliverable, Project } from '../../types/portal';
interface Row {
  key: string;
  projectId: string;
  deliverableId: string;
  /** Priority: lower is more actionable. */
  rank: number;
  /** e.g. "Smart Cities approved" */
  headline: string;
  /** e.g. "BLOOMBERG · V3 · APPROVED BY MORGAN" */
  detail: string;
  /** e.g. "Ready for Portal to publish" */
  next: string;
}
function detailLine(project: Project, parts: (string | undefined)[]): string {
  return [project.client, ...parts].filter(Boolean).join(' · ');
}

/**
 * What the studio has to act on. Each line names the deliverable, the client,
 * its current state and Portal's next move — derived from live state.
 */
export function NeedsAttention() {
  const {
    activeProjects,
    openActivity
  } = usePortal();
  const rows: Row[] = [];
  const push = (project: Project, item: Deliverable, row: Omit<Row, 'key' | 'projectId' | 'deliverableId'>) => rows.push({
    ...row,
    key: `${item.id}-${row.rank}`,
    projectId: project.id,
    deliverableId: item.id
  });
  for (const project of activeProjects) {
    for (const item of project.deliverables) {
      /* Only genuine shared records count — prototype seed data never does. */
      const outstanding = item.changeRequests.filter(request => !request.resolved && request.shared);
      if (outstanding.length > 0) {
        push(project, item, {
          rank: 1,
          headline: `${item.name} — changes requested`,
          detail: detailLine(project, [item.version, `${outstanding.length} outstanding change${outstanding.length === 1 ? '' : 's'}`]),
          next: 'Action the changes, then publish a new version'
        });
        continue;
      }

      /* An approval only lands here when a real client actually gave it. */
      if (item.status === 'Approved' && item.approvedBy) {
        push(project, item, {
          rank: 2,
          headline: `${item.name} approved`,
          detail: detailLine(project, [item.version, `Approved by ${item.approvedBy}`]),
          next: 'Ready for Portal to publish the final files'
        });
      }
    }
  }
  rows.sort((a, b) => a.rank - b.rank);
  return <section aria-labelledby="needs-attention" className="border border-paper/25 px-7 py-10 sm:px-12 sm:py-12" data-id="element-311" data-mp="k2r7g1xwan7r">
      <h2 id="needs-attention" className="label m-0 text-paper/40" data-id="element-312" data-mp="yqc8zrf4gedk">
        Needs Attention
      </h2>

      {rows.length === 0 ? <p className="mt-7 font-display text-3xl leading-tight sm:text-4xl" data-id="element-313" data-mp="ts11hncaufyi">
          Nothing waiting.
        </p> : <ol className="m-0 mt-7 list-none p-0" data-id="element-314" data-mp="12sz3k036gdeg">
          {rows.map((row, index) => <li key={row.key} data-id="element-315" data-mp="12r5zmsjy87a5" data-mpi={index}>
              <button type="button" onClick={() => openActivity({
          id: row.key,
          projectId: row.projectId,
          projectName: '',
          actorName: '',
          action: '',
          deliverableId: row.deliverableId,
          at: Date.now()
        })} className="flex w-full items-baseline gap-x-5 py-2.5 text-left transition-opacity duration-150 ease-out hover:opacity-70" data-id="element-316" data-mp="39ix0w2e5o0oe">
                <span className="font-display text-3xl leading-none sm:text-4xl" data-id="element-317" data-mp="11c37vptyppsi">{index + 1}</span>
                <span className="min-w-0" data-id="element-318" data-mp="16amw5ww13do1">
                  <span className="block font-display text-xl leading-none text-paper/70 sm:text-2xl" data-id="element-319" data-mp="16bpnu716dk50">
                    {row.headline}
                  </span>
                  <span className="label mt-2.5 block text-paper/40" data-id="element-320" data-mp="wc71xl1pfoab">{row.detail}</span>
                  <span className="label-sm mt-1.5 block text-paper/30" data-id="element-321" data-mp="2mpfpz88qhyc0">{row.next}</span>
                </span>
              </button>
            </li>)}
        </ol>}
    </section>;
}